import { useState, useEffect } from 'react';
import { Plus, Pencil, UserX, UserCheck, Key, Loader2, X } from 'lucide-react';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, fieldStyle, selectStyle, navyBtn, outlineBtn,
} from '../../styles/tokens';
import client from '../../api/client';
import { useAuth } from '../../context/AuthContext';

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');

  // Form fields
  const [formName, setFormName]         = useState('');
  const [formEmail, setFormEmail]       = useState('');
  const [formRoles, setFormRoles]       = useState(['employee']);
  const [formPassword, setFormPassword] = useState('');
  const [formManagerId, setFormManagerId] = useState('');

  // Reset password
  const [resetTarget, setResetTarget]     = useState(null);
  const [resetPassword, setResetPw]       = useState('');
  const [resetting, setResetting]         = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  async function fetchUsers() {
    try {
      const res = await client.get('/api/admin/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }

  function openCreateModal() {
    setEditingUser(null);
    setFormName('');
    setFormEmail('');
    setFormRoles(['employee']);
    setFormPassword('');
    setFormManagerId('');
    setError('');
    setShowModal(true);
  }

  function openEditModal(u) {
    setEditingUser(u);
    setFormName(u.name);
    setFormEmail(u.email);
    setFormRoles(u.roles || ['employee']);
    setFormPassword('');
    setFormManagerId(u.manager_id || '');
    setError('');
    setShowModal(true);
  }

  function toggleFormRole(role) {
    setFormRoles(prev => {
      if (prev.includes(role)) {
        const next = prev.filter(r => r !== role);
        return next.length === 0 ? prev : next;
      }
      return [...prev, role];
    });
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      if (editingUser) {
        await client.patch(`/api/admin/users/${editingUser.id}`, {
          name: formName, email: formEmail, roles: formRoles,
          manager_id: formManagerId ? Number(formManagerId) : null,
        });
      } else {
        if (!formPassword) { setError('La contraseña es requerida'); setSaving(false); return; }
        await client.post('/api/admin/users', {
          name: formName, email: formEmail, roles: formRoles, password: formPassword,
          manager_id: formManagerId ? Number(formManagerId) : null,
        });
      }
      setShowModal(false);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el usuario');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(u) {
    try {
      await client.patch(`/api/admin/users/${u.id}`, { active: !u.active });
      await fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar el usuario');
    }
  }

  async function handleResetPassword() {
    if (!resetPassword) return;
    setResetting(true);
    try {
      await client.post(`/api/admin/users/${resetTarget.id}/reset-password`, { password: resetPassword });
      setResetTarget(null);
      setResetPw('');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al restablecer la contraseña');
    } finally {
      setResetting(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando usuarios…
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0 }}>Gestión de Usuarios</h2>
        <button onClick={openCreateModal} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Crear Usuario
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...fieldStyle, width: 220 }}
          placeholder="Buscar por nombre o correo..."
        />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ ...selectStyle, width: 'auto' }}>
          <option value="all">Todos los Roles</option>
          <option value="employee">Chofer</option>
          <option value="supervisor">Supervisor</option>
          <option value="admin">Administrador</option>
          <option value="reviewer">Reviewer</option>
        </select>
        <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)} style={{ ...selectStyle, width: 'auto' }}>
          <option value="all">Todos</option>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
        </select>
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setShowModal(false)}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '24px 28px',
            width: '90%', maxWidth: 500, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: 0 }}>
                {editingUser ? `Editar Usuario — ${editingUser.name}` : 'Crear Nuevo Usuario'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Nombre</label>
                <input value={formName} onChange={e => setFormName(e.target.value)} style={fieldStyle} placeholder="Nombre completo" />
              </div>
              <div>
                <label style={labelStyle}>Correo electrónico</label>
                <input type="email" value={formEmail} onChange={e => setFormEmail(e.target.value)} style={fieldStyle} placeholder="email@example.com" />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>Roles</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', padding: '8px 0' }}>
                {[
                  { key: 'employee',   label: 'Chofer' },
                  { key: 'supervisor', label: 'Supervisor(a)' },
                  { key: 'engineer',   label: 'Ingeniero(a)' },
                  { key: 'manager',    label: 'Gerente' },
                  { key: 'reviewer',   label: 'Revisión Final' },
                  { key: 'accountant', label: 'Contabilidad' },
                  { key: 'admin',      label: 'Administrador' },
                ].map(r => (
                  <label key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: NAVY }}>
                    <input
                      type="checkbox"
                      checked={formRoles.includes(r.key)}
                      onChange={() => toggleFormRole(r.key)}
                      style={{ accentColor: AMBER, width: 16, height: 16 }}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <label style={labelStyle}>Gerente (para PTO L2)</label>
                <select value={formManagerId} onChange={e => setFormManagerId(e.target.value)} style={{ ...fieldStyle, appearance: 'auto' }}>
                  <option value="">— Sin gerente —</option>
                  {users.filter(u => u.active && u.id !== editingUser?.id).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              {!editingUser && (
                <div>
                  <label style={labelStyle}>Contraseña</label>
                  <input type="password" value={formPassword} onChange={e => setFormPassword(e.target.value)} style={fieldStyle} placeholder="Contraseña inicial" />
                </div>
              )}
            </div>

            {error && (
              <div style={{
                marginTop: 12, padding: '8px 12px', background: '#fef2f2', color: '#991b1b',
                border: '1px solid #fecaca', borderRadius: 6, fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              {editingUser && (
                <button onClick={() => { toggleActive(editingUser); setShowModal(false); }} style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 6, padding: '6px 12px', fontSize: 11,
                  color: editingUser.active ? '#ef4444' : '#16a34a',
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>
                  {editingUser.active ? 'Desactivar' : 'Activar'}
                </button>
              )}
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <button onClick={() => setShowModal(false)} style={{
                  background: 'none', border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 20, padding: '6px 16px', fontSize: 12,
                  color: '#64748b', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={saving} style={{
                  ...navyBtn, padding: '6px 16px', fontSize: 12,
                  opacity: saving ? 0.6 : 1,
                }}>
                  {saving ? 'Guardando…' : (editingUser ? 'Actualizar Usuario' : 'Crear Usuario')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ ...bubbleStyle, width: 400, maxWidth: '90vw' }}>
            <h3 style={{ fontSize: 15, fontWeight: '700', color: NAVY, marginBottom: 12 }}>
              Restablecer Contraseña — {resetTarget.name}
            </h3>
            <label style={labelStyle}>Nueva Contraseña</label>
            <input type="password" value={resetPassword} onChange={e => setResetPw(e.target.value)} style={fieldStyle} placeholder="Nueva contraseña" />
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <button onClick={handleResetPassword} disabled={resetting} style={{ ...navyBtn, opacity: resetting ? 0.6 : 1 }}>
                {resetting ? 'Restableciendo…' : 'Restablecer Contraseña'}
              </button>
              <button onClick={() => { setResetTarget(null); setResetPw(''); }} style={outlineBtn}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div style={bubbleStyle}>
        {/* Header row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1.2fr 130px 100px 90px 160px',
          gap: 8, padding: '0 0 12px', borderBottom: `1px solid ${BORDER_COLOR}`,
          fontSize: 11, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <span>Nombre</span>
          <span>Correo</span>
          <span>Roles</span>
          <span>Gerente</span>
          <span>Estado</span>
          <span style={{ textAlign: 'right' }}>Acciones</span>
        </div>

        {users.filter(u => {
          const term = search.toLowerCase();
          if (term && !u.name.toLowerCase().includes(term) && !u.email.toLowerCase().includes(term)) return false;
          const roles = u.roles || [];
          if (roleFilter !== 'all' && !roles.includes(roleFilter)) return false;
          if (activeFilter === 'active' && !u.active) return false;
          if (activeFilter === 'inactive' && u.active) return false;
          return true;
        }).map(u => {
          const roles = u.roles || [];
          const isAdminUser = roles.includes('admin');
          return (
            <div key={u.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 1.2fr 130px 100px 90px 160px',
              gap: 8, padding: '12px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
              alignItems: 'center', fontSize: 13,
              opacity: u.active ? 1 : 0.55,
              cursor: 'pointer',
            }} onClick={() => openEditModal(u)}>
              <span style={{ fontWeight: '600', color: NAVY }}>{u.name}</span>
              <span style={{ color: '#475569' }}>{u.email}</span>
              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {roles.map(rk => {
                  const cfg = rk === 'admin'
                    ? { label: 'Admin',           bg: 'rgba(217,119,6,0.12)', color: AMBER }
                    : rk === 'supervisor'
                    ? { label: 'Supervisor(a)',   bg: '#dbeafe',              color: '#1e40af' }
                    : rk === 'engineer'
                    ? { label: 'Ingeniero(a)',    bg: '#d1fae5',              color: '#065f46' }
                    : rk === 'manager'
                    ? { label: 'Gerente',         bg: '#e0e7ff',              color: '#3730a3' }
                    : rk === 'reviewer'
                    ? { label: 'Rev. Final',      bg: '#ede9fe',              color: '#6d28d9' }
                    : rk === 'accountant'
                    ? { label: 'Contabilidad',    bg: '#fef3c7',              color: '#92400e' }
                    : { label: 'Chofer',          bg: '#f0fdf4',              color: '#166534' };
                  return (
                    <span key={rk} style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
                      fontSize: 10, fontWeight: '600', background: cfg.bg, color: cfg.color,
                    }}>
                      {cfg.label}
                    </span>
                  );
                })}
              </span>
              <span style={{ fontSize: 12, color: '#475569' }}>{u.manager_name || '—'}</span>
              <span>
                <span style={{
                  display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                  fontSize: 11, fontWeight: '600',
                  background: u.active ? '#f0fdf4' : '#fef2f2',
                  color: u.active ? '#166534' : '#991b1b',
                }}>
                  {u.active ? 'Activo' : 'Inactivo'}
                </span>
              </span>
              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                <button onClick={() => openEditModal(u)} title="Editar" style={actionBtn}>
                  <Pencil size={13} />
                </button>
                <button onClick={() => toggleActive(u)} title={u.active ? 'Desactivar' : 'Activar'} style={actionBtn}>
                  {u.active ? <UserX size={13} /> : <UserCheck size={13} />}
                </button>
                <button onClick={() => setResetTarget(u)} title="Restablecer Contraseña" style={actionBtn}>
                  <Key size={13} />
                </button>
              </div>
            </div>
          );
        })}

        {users.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No se encontraron usuarios
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: '600', color: '#475569', marginBottom: 4,
};

const actionBtn = {
  background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
  padding: '6px 8px', cursor: 'pointer', color: NAVY, display: 'inline-flex', alignItems: 'center',
};
